"use strict";
// Copyright (c) 2021-2024, Brandon Lehmann <brandonlehmann@gmail.com>
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const buffer_1 = require("../modules/buffer");
const nanoid_1 = require("nanoid");
($ => {
    $.createFloatingInputGroup = (options = {}) => {
        var _a, _b, _c, _d;
        (_a = options.type) !== null && _a !== void 0 ? _a : (options.type = 'text');
        (_b = options.readonly) !== null && _b !== void 0 ? _b : (options.readonly = false);
        (_c = options.disabled) !== null && _c !== void 0 ? _c : (options.disabled = false);
        const id = options.id || ((_d = options.input) === null || _d === void 0 ? void 0 : _d.id()) || (0, nanoid_1.nanoid)();
        const float = $('<div>')
            .addClass('form-floating input-group');
        const label = $('<label>');
        if (options.label) {
            if (typeof options.label === 'string') {
                label.text(options.label);
            }
            else {
                options.label.appendTo(label);
            }
        }
        if (id) {
            label.attr('for', id);
            if (options.type === 'file') {
                float.removeClass('form-floating input-group');
                label.addClass('form-label')
                    .appendTo(float);
            }
        }
        if (!options.input) {
            const element = $('<input>')
                .attr('type', options.type);
            if (id) {
                element.attr('id', id);
            }
            if (options.class) {
                element.addClass(options.class);
            }
            if (options.readonly) {
                element.attr('readonly', 'readonly');
            }
            if (options.disabled) {
                element.attr('disabled', 'disabled');
            }
            for (const key of Object.keys(options)) {
                if (key === 'id' || key === 'label' || key === 'class' || key === 'readonly' || key === 'disabled') {
                    continue;
                }
                const value = options[key];
                if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                    const _value = value.toString().trim();
                    if (_value && _value.length !== 0) {
                        element.attr(key, _value);
                    }
                }
                else if (value) {
                    value.appendTo(element);
                }
            }
            element.addClass('form-control')
                .appendTo(float);
        }
        else {
            if (id) {
                options.input.attr('id', id);
            }
            options.input.addClass('form-control')
                .appendTo(float);
        }
        if (label.attr('for') && options.type !== 'file') {
            label.appendTo(float);
        }
        return float;
    };
    $.createMedia = (src, options = {}) => {
        if (options.autoplay) {
            options.muted = true;
        }
        const element = $('<video>')
            .prop(Object.assign({ src }, options));
        for (const key of Object.keys(options)) {
            if (options[key] === true) {
                element.attr(key, '');
            }
        }
        return element;
    };
    $.hasClass = (className) => {
        className = className.trim();
        if (className.length === 0)
            return false;
        const sheets = document.styleSheets;
        for (let i = 0; i < sheets.length; i++) {
            const rules = sheets[i].rules || sheets[i].cssRules;
            for (let j = 0; j < rules.length; j++) {
                const rule = rules[j];
                if (rule instanceof CSSStyleRule && rules[j].selectorText.includes(`.${className}`)) {
                    return true;
                }
            }
        }
        return false;
    };
    $.nanoid = (size) => (0, nanoid_1.nanoid)(size);
    $.theme = (mode) => {
        const $html = $('html');
        const theme = $html.attr('data-bs-theme');
        if (theme === 'dark' || theme === 'light') {
            mode !== null && mode !== void 0 ? mode : (mode = theme);
        }
        else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
            mode !== null && mode !== void 0 ? mode : (mode = 'dark');
        }
        else {
            mode !== null && mode !== void 0 ? mode : (mode = 'light');
        }
        $html.attr('data-bs-theme', mode);
        return mode;
    };
    $.fn.element = function () {
        return $(this)[0];
    };
    $.fn.fileContents = function () {
        return __awaiter(this, arguments, void 0, function* (idx = 0) {
            const $this = $(this);
            const files = $this.prop('files');
            if (!files || files.length === 0) {
                throw new Error('Could not read input field');
            }
            const file = files[idx];
            if (!file) {
                throw new Error('Could not read input file');
            }
            const content = buffer_1.Buffer.from(yield file.arrayBuffer());
            const { size, type, name, lastModified, webkitRelativePath } = file;
            return {
                name,
                type,
                size,
                lastModified: new Date(lastModified),
                webkitRelativePath,
                content
            };
        });
    };
    $.fn.fileCount = function () {
        const $this = $(this);
        return $this.prop('files').length || 0;
    };
    $.fn.fullHTML = function () {
        const $this = $(this);
        return $('<div>')
            .append($this.clone())
            .html();
    };
    $.fn.id = function () {
        const $this = $(this);
        const id = $this.attr('id') || (0, nanoid_1.nanoid)();
        $this.attr('id', id);
        return id;
    };
    $.fn.path = function (type = 'id') {
        let $this = $(this);
        const ids = [];
        do {
            if (type === 'tagName') {
                const id = $this.prop(type);
                if (id) {
                    ids.push(id);
                }
            }
            else {
                ids.push($this.id());
            }
            $this = $this.parent();
        } while (typeof $this.prop('tagName') !== 'undefined');
        return ids.reverse()
            .map(elem => `${type === 'id' ? '#' : ''}${elem}`)
            .join(type === 'id' ? ' ' : ' > ');
    };
    $.fn.type = function () {
        return $(this).prev().prop('nodeName');
    };
})(window.$);
//# sourceMappingURL=html.js.map